import { useEffect, useRef, useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';

type WSMessage = {
  type: 'new_message' | 'new_match' | 'typing' | 'presence';
  [key: string]: any;
};

export type MatchNotification = {
  matchId: number;
  matchedWith: {
    id: number;
    name: string;
    age?: number;
    city?: string;
    photo?: string | null;
  };
  message: string;
  timestamp: string;
};

type MatchCallback = (data: MatchNotification) => void;

const matchCallbacks = new Set<MatchCallback>();
const subscriptionRefreshCallbacks = new Set<() => void>();

export function onGlobalMatch(cb: MatchCallback) {
  matchCallbacks.add(cb);
  return () => {
    matchCallbacks.delete(cb);
  };
}

export function onGlobalSubscriptionRefresh(cb: () => void) {
  subscriptionRefreshCallbacks.add(cb);
  return () => {
    subscriptionRefreshCallbacks.delete(cb);
  };
}

export function useWebSocket() {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [connected, setConnected] = useState(false);
  const listenersRef = useRef<Map<string, Set<(data: any) => void>>>(new Map());

  const connect = useCallback(() => {
    if (!token) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const basePath = import.meta.env.BASE_URL.replace(/\/$/, '');
    const wsUrl = `${protocol}//${host}${basePath}/api/ws?token=${token}`;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    };

    ws.onmessage = event => {
      try {
        const data: WSMessage = JSON.parse(event.data);

        if (data.type === 'new_message') {
          queryClient.invalidateQueries({ queryKey: ['getMessages', data.matchId] });
          queryClient.invalidateQueries({ queryKey: ['getMatches'] });
        } else if (data.type === 'new_match') {
          queryClient.invalidateQueries({ queryKey: ['getMatches'] });
          queryClient.invalidateQueries({ queryKey: ['/api/discover'] });

          for (const cb of matchCallbacks) {
            try {
              cb(data as unknown as MatchNotification);
            } catch {}
          }

          for (const cb of subscriptionRefreshCallbacks) {
            try { cb(); } catch {}
          }
        }

        const listeners = listenersRef.current.get(data.type);
        if (listeners) {
          listeners.forEach(cb => cb(data));
        }
      } catch {}
    };

    ws.onclose = () => {
      setConnected(false);
      reconnectTimer.current = setTimeout(connect, 3000);
    };

    ws.onerror = () => {
      ws.close();
    };
  }, [token, queryClient]);

  useEffect(() => {
    connect();
    return () => {
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
    };
  }, [connect]);

  const sendTyping = useCallback((matchId: number) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'typing', matchId }));
    }
  }, []);

  const on = useCallback((type: string, callback: (data: any) => void) => {
    if (!listenersRef.current.has(type)) {
      listenersRef.current.set(type, new Set());
    }
    listenersRef.current.get(type)!.add(callback);
    return () => {
      listenersRef.current.get(type)?.delete(callback);
    };
  }, []);

  return { connected, sendTyping, on };
}
